import React, { createContext, useContext } from "react";
import type { ReactNode } from "react";
import useSWR from "swr";
import { getApiV1Config } from "@/lib/api/sdk-facade.gen";
import type { AppConfig, AppConfigContextType } from "@/lib/types/app-config";
import { useAuth } from "@/contexts/auth-context";
import { APP_CONFIG_SWR_KEY } from "@/lib/context-cache-keys";

const AppConfigContext = createContext<AppConfigContextType | null>(null);

async function configFetcher(): Promise<AppConfig> {
  return await getApiV1Config();
}

type AppConfigProviderProps = {
  children: ReactNode;
};

export function AppConfigProvider({ children }: AppConfigProviderProps) {
  const { bootstrapState } = useAuth();
  const shouldFetch = bootstrapState === "authenticated";

  const { data, error, isLoading, mutate } = useSWR<AppConfig, Error>(
    shouldFetch ? APP_CONFIG_SWR_KEY : null,
    configFetcher,
    {
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      dedupingInterval: 5000,
      shouldRetryOnError: false,
    },
  );

  const chatEnabled = data?.features.chat.enabled === true;

  const value: AppConfigContextType = {
    config: data ?? null,
    chatEnabled,
    isLoading: shouldFetch && isLoading && data == null,
    error: error ?? null,
    refresh: async () => {
      await mutate();
    },
  };

  return <AppConfigContext.Provider value={value}>{children}</AppConfigContext.Provider>;
}

export function useAppConfig(): AppConfigContextType {
  const context = useContext(AppConfigContext);

  if (!context) {
    throw new Error("useAppConfig must be used within an AppConfigProvider");
  }

  return context;
}
